/**
 * DocumentDialog Component (Refactored)
 * Container component for creating and editing documents
 *
 * Migrated to component architecture (Phase 2.1)
 * - Extracted presentational components
 * - Business logic in custom hooks
 * - Clean composition pattern
 */

import React from 'react';
import { useTranslation } from 'react-i18next';
import {
  Button,
  Box,
  Typography,
  FormControl,
  InputLabel,
  Select,
  MenuItem
} from '@mui/material';
import Grid from '@mui/material/Grid';

import { BaseDialog } from '../../ui/BaseDialog';
import { VisibilitySection } from '../../ui/VisibilitySection';
import { SharingSection } from '../../ui/SharingSection';
import { log } from '../../../utils/errors/logger';

// Import extracted components
import { CreateDocumentTabs } from './components/CreateDocumentTabs';
import { TextContentTab } from './components/TextContentTab';
import { FileUploadTab } from './components/FileUploadTab';
import { UrlImportTab } from './components/UrlImportTab';
import { EditFileSection } from './components/EditFileSection';
import { EditTextSection } from './components/EditTextSection';
import { EditUrlSection } from './components/EditUrlSection';
import { DocumentFormFields } from './components/DocumentFormFields';

// Import hook
import { useDocumentDialog } from '../../../hooks/documents/useDocumentDialog';

// Import types
import { DocumentDialogProps } from './types';

/**
 * Main DocumentDialog container component
 * Orchestrates sub-components and delegates logic to custom hook
 */
export const DocumentDialog: React.FC<DocumentDialogProps> = ({
  open,
  onClose,
  editingDoc,
  onSubmit,
  availableProviders,
  defaultProvider
}) => {
  const { t } = useTranslation();

  // Use custom hook for all business logic
  const {
    isEditMode,
    formState,
    handleFormChange,
    handleSelectChange,
    selectedFile,
    handleFileSelect,
    handleFileBrowse,
    handleFileRemove,
    uploadProgress,
    uploadingFile,
    fileInputRef,
    fileError,
    maxFileSizeMB,
    activeTab,
    handleTabChange,
    handleSubmit,
    isSubmitting,
    submitError,
    isValid,
    fullDocument,
    handleVisibilityChange,
    handleSharingChange
  } = useDocumentDialog({
    open,
    editingDoc,
    defaultProvider,
    onClose,
    onSubmit
  });

  return (
    <BaseDialog
      open={open}
      onClose={onClose}
      title={isEditMode ? t('documents.dialog.edit') : t('documents.dialog.addNew')}
      maxWidth="md"
      fullWidth
      loading={isSubmitting}
      error={submitError || undefined}
      actions={
        <>
          <Button onClick={onClose} disabled={uploadingFile || isSubmitting}>
            {t('documents.dialog.cancel')}
          </Button>
          <Button
            onClick={handleSubmit}
            variant="contained"
            disabled={!isValid}
          >
            {uploadingFile
              ? t('documents.dialog.uploading')
              : isEditMode
              ? t('documents.dialog.update')
              : t('documents.dialog.add')}
          </Button>
        </>
      }
    >
      {/* CREATE MODE: Document source tabs and content */}
      {!isEditMode && (
        <>
          <CreateDocumentTabs value={activeTab} onChange={handleTabChange} />

          <Box sx={{ mb: 3 }}>
            {/* Text content tab */}
            {activeTab === 0 && (
              <TextContentTab
                value={formState.content}
                onChange={handleFormChange}
              />
            )}

            {/* File upload tab */}
            {activeTab === 1 && (
              <FileUploadTab
                selectedFile={selectedFile}
                onFileSelect={handleFileSelect}
                onBrowse={handleFileBrowse}
                uploading={uploadingFile}
                progress={uploadProgress}
                fileInputRef={fileInputRef}
                fileError={fileError}
                maxFileSizeMB={maxFileSizeMB}
              />
            )}

            {/* URL tab */}
            {activeTab === 2 && (
              <UrlImportTab
                value={formState.url}
                onChange={handleFormChange}
              />
            )}
          </Box>
        </>
      )}

      {/* Common fields for all document types */}
      <DocumentFormFields
        title={formState.title}
        onTitleChange={handleFormChange}
      />

      {/* Permissions and Privacy Settings */}
      <Typography variant="subtitle1" sx={{ mt: 3, mb: 2, fontWeight: 600 }}>
        {t('documents.dialog.permissionsAndPrivacy')}
      </Typography>

      <Grid container spacing={2}>
        <Grid size={12}>
          <VisibilitySection
            visibility={formState.visibility}
            onChange={handleVisibilityChange}
          />
        </Grid>

        <Grid size={12}>
          <SharingSection
            title=""
            userIds={formState.shared_with_users}
            groupIds={formState.shared_with_groups}
            onChange={handleSharingChange}
          />
        </Grid>

        {/* Processing Settings - only show for new documents */}
        {!isEditMode && (
          <Grid size={{ xs: 12, sm: 6 }}>
            <Typography variant="subtitle1" sx={{ mb: 2, fontWeight: 600 }}>
              {t('documents.dialog.processingSettings')}
            </Typography>
            <FormControl fullWidth margin="normal">
              <InputLabel>{t('documents.dialog.textExtractionProvider')}</InputLabel>
              <Select
                name="text_extraction_provider"
                value={formState.text_extraction_provider}
                onChange={handleSelectChange}
                label={t('documents.dialog.textExtractionProvider')}
              >
                {availableProviders.map((provider) => (
                  <MenuItem key={provider.value} value={provider.value}>
                    {provider.label}
                    {provider.value === defaultProvider && ' (Default)'}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
        )}
      </Grid>

      {/* EDIT MODE: Show appropriate UI based on document type */}
      {isEditMode && editingDoc && (
        <>
          {/* DEBUG: Log document info */}
          {(() => {
            log.debug('DocumentDialog edit mode', {
              document_id: editingDoc.document_id || '',
              source_type: String(editingDoc.source_type || ''),
              has_full_document: Boolean(fullDocument)
            });
            return null;
          })()}

          {/* For file documents: Show file replacement option */}
          {editingDoc.source_type === 'file' && (
              <EditFileSection
                selectedFile={selectedFile}
                onFileSelect={handleFileSelect}
                onBrowse={handleFileBrowse}
                onRemove={handleFileRemove}
                fileInputRef={fileInputRef}
              />
            )}

          {/* For text documents: Show content editor */}
          {(editingDoc.source_type === 'manual' || editingDoc.source_type === 'custom') && (
            <EditTextSection
              content={formState.content}
              onChange={handleFormChange}
            />
          )}

          {/* For URL documents: Show URL editor */}
          {editingDoc.source_type === 'url' && (
            <EditUrlSection
              url={formState.url}
              onChange={handleFormChange}
            />
          )}
        </>
      )}
    </BaseDialog>
  );
};
